<template>
  <BaseModal
    :heading="$t('marketing.discounts.enable-last-minute-discount-modal.setup')"
    :loading="isLoading"
    small
    :parentRoute="{ name: 'treatwell-admin' }"
  >
    <Flex direction="column">
      <BaseText>
        {{ $t('marketing.discounts.enable-last-minute-discount-modal.tw_info') }}
      </BaseText>
      <BaseText :href="helpCenterArticleUrl(5669294)">
        {{ $t('marketing.discounts.more_tips') }}
      </BaseText>
      <BaseAlert
        v-if="hasFeatureFlag('module-marketing')"
        color="warning"
        :text="
        $t('admin.treatwell.main.getting_seen.also_applies_to_booking_widget', brandName())
      "
      />
      <Flex
        equalWidth
        :direction="$screen === 's' ? 'column' : 'row'"
      >
        <BaseDropdown
          v-model="discountPercentage"
          :label="
            $t(
              'marketing.discounts.enable-last-minute-discount-modal.apply_percentage'
            )
          "
          :options="percentageOptions"
          v-test="'percentage-options'"
        />
        <BaseDropdown
          v-model="discountWindow"
          :label="
            $t(
              'marketing.discounts.enable-last-minute-discount-modal.for_bookings_made'
            )
          "
          :options="discountWindowOptions"
          v-test="'discount-window-options'"
        />
      </Flex>
    </Flex>
    <template #footerSub>
      <BaseText
        v-if="discountEnabled"
        :routerLink="{ name: 'treatwell-salon-disable-last-minute-discount' }"
        v-test="'last-minute-discount-disable'"
      >
        {{ $t('global.actions.disable') }}
      </BaseText>
    </template>
    <template #footer>
      <BaseButton
        color="inverted"
        @click="close"
        v-test="'discountCodeCancel'"
      >
        {{ $t('global.actions.cancel') }}
      </BaseButton>

      <BaseButton
        :loading="isSaving"
        @click="onSubmit"
        v-test="'saveButton'"
      >
        {{ $t('global.apply') }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script
  setup
  lang="ts"
>
import { storeToRefs } from 'pinia';
import { brandName } from '@/helpers/brand-name';
import { helpCenterArticleUrl } from '@/helpers/help-center';
import { flash } from '@/helpers/ui';
import { useCompanyStore } from '@/stores/company';

import { useUserStore } from '@/stores/user';

const { hasFeatureFlag } = useUserStore();

import { useRouter } from 'vue-router';

const router = useRouter();

import { useMutation, useQuery } from '@vue/apollo-composable';
import { useI18n } from 'vue-i18n';
import {
  GET_LAST_MINUTE_DISCOUNTS,
  UPDATE_LAST_MINUTE_DISCOUNTS,
} from '../graphql';

const { t } = useI18n();

const { companySettings } = storeToRefs(useCompanyStore());

const discountPercentage = ref(10);
const discountWindow = ref('TWO_HOURS');
const discountEnabled = computed(
  () => companySettings.value.bookings.lastMinuteDiscountEnabled,
);
const percentageOptions = [5, 10, 15, 20, 30, 50].map((percentage) => ({
  value: percentage,
  label: `${percentage}%`,
}));

const discountWindowOptions = [
  'ONE_HOUR',
  'TWO_HOURS',
  'FOUR_HOURS',
  'ONE_DAY_FROM_NOW',
  'TWO_DAYS_FROM_NOW',
  'SAME_DAY',
].map((option) => ({
  value: option,
  label: t(`marketing.discounts.booking_range.${option.toLowerCase()}`),
}));

const close = () => {
  router.push({ name: 'treatwell-admin' });
};

const { loading: isLoading, onResult } = useQuery(GET_LAST_MINUTE_DISCOUNTS);
onResult((response) => {
  discountPercentage.value =
    response.data.lastMinuteDiscounts.discountPercentage;
  discountWindow.value = response.data.lastMinuteDiscounts.discountWindow;
});

const { mutate, loading: isSaving } = useMutation(
  UPDATE_LAST_MINUTE_DISCOUNTS,
  {
    update(cache) {
      cache.evict({ id: 'ROOT_QUERY', fieldName: 'lastMinuteDiscounts' });
      cache.gc();
    },
  },
);

const onSubmit = () => {
  mutate({
    input: {
      discountPercentage: discountPercentage.value,
      discountWindow: discountWindow.value,
      enabled: true,
    },
  }).then(() => {
    companySettings.value.bookings.lastMinuteDiscountEnabled = true;
    flash(t('global.flash.last_minute_discount_enabled'));
    close();
  });
};
</script>
